import { TAU } from '../core/math';
import { RNG, rr } from '../core/rng';
import { SFX } from '../platform/audio';
import { STATE, spawnRing } from './fx';
import { banner, radio } from '../ui/hud';
import { P } from '../world/player';
import { WAVES, waveById } from '../data/waves';
import { updateStream } from './formations';
/* ============================================================
   波次引擎 —— 排程、預告、梯次
   ============================================================

   劇本在 `data/waves.ts`，形狀在 `sim/formations.ts`，這裡只做三件事：

   1. **什麼時候來**：間隔隨時間縮短（46 → 38 → 30 → 24 秒）
   2. **怎麼預告**：方位性的預告 —— 三個由遠而近的警示環指向來襲方向，
      加上一句無線電。舊版的預告是「玩家腳下一個大圈」，
      它只說「有事」不說「從哪來」，玩家看完還是只能亂跑
   3. **梯次怎麼放**：一個以模擬時間計時的小佇列。用 `fx.ts` 的 `schedule`
      也可以，但那個佇列在 `clearEntities()` 會被清掉 ——
      波次要能跨越換幕與 Boss 降臨，所以自己持有

   **不要把劇本寫進這個檔**。分開的好處在下一次擴充時才看得到：
   加一種波次應該是往 `data/waves.ts` 加一個物件，不是改引擎。 */

export var WAVE_FIRST = 88;

/** 波次間隔。**前段刻意保守、後段才密**。

    第一版全程 42 秒起跳，實測（5 顆種子的壓力診斷）bot 全部死在 2.4–4.5 分，
    而改動前的中位數是 7.7 分 —— 一波現在是三個梯次、總量比舊編隊大得多，
    在同屏才 25 隻的第 2 分鐘等於把場上的敵人翻倍。
    密度要往上是對的（診斷 3），但**開場那三分鐘是玩家認識武裝的時間**。 */
export function waveEvery(t) {
  return t > 900 ? 24 : t > 600 ? 30 : t > 300 ? 38 : 46;
}

export var WS = {
  next: 0, count: 0, last: '', warned: false, warnT: 0,
  a: 0, cur: '', seen: [] as string[], q: [] as any[]
};

export function resetWaves() {
  WS.next = WAVE_FIRST; WS.count = 0; WS.last = ''; WS.warned = false; WS.warnT = 0;
  WS.a = 0; WS.cur = ''; WS.seen = []; WS.q = [];
}

/** 八方位。無線電要講得出「從哪邊來」，玩家才有辦法在 1 秒內做決定。
    座標約定：+x 東、+z 南（畫面下方），與相機一致。 */
var DIRS = ['東', '東南', '南', '西南', '西', '西北', '北', '東北'];
export function bearing(a) {
  var i = Math.round(((a % TAU) + TAU) % TAU / (TAU / 8)) % 8;
  return DIRS[i];
}

/** 方位預告：由遠而近的三個環，指向來襲方向。
    **這是這一版最重要的一行視覺** —— 預告要能被「讀」而不只是被「看到」。 */
function telegraph(a) {
  /* 四個由遠而近、**越近越小**的標記，讀起來是一支指向玩家的箭頭。

     實看調過兩件事：①第一版是三個直徑 9 的大環，加法混色打在亮地板上
     幾乎變成白色，看起來像場景裝飾不像警告；②環會擴張，
     擴張中的圓讀成「這裡發生了什麼」而不是「東西要從這裡來」。
     現在的做法是擴張幅度極小（近似定點標記）、尺寸遞減、顏色更飽和。 */
  for (var i = 0; i < 4; i++) {
    var d = 38 - i * 8;
    var s = 5.4 - i * 1.1;
    spawnRing(P.pos.x + Math.cos(a) * d, P.pos.z + Math.sin(a) * d,
      0xff3a08, s * 0.72, s, 0.95 - i * 0.1);
  }
  SFX.alarm();
}

/** 這一刻可以出哪些波。不重複上一種 —— 連兩次同樣的波會讓它從事件退化成背景。 */
function pick(t) {
  var ok = WAVES.filter(function (w) { return t >= w.from && w.id !== WS.last; });
  if (!ok.length) ok = WAVES.filter(function (w) { return t >= w.from; });
  if (!ok.length) return WAVES[0];
  return ok[Math.floor(RNG.next() * ok.length) % ok.length];
}

/** 把一整份劇本排進佇列。梯次的 `at` 是「從現在起幾秒」。 */
function launch(w, a) {
  var c = { a: a, dir: bearing(a), t: STATE.t };
  for (var i = 0; i < w.ech.length; i++) {
    WS.q.push({ t: w.ech[i].at, run: w.ech[i].run, c: c });
  }
  WS.last = w.id; WS.count++;
  if (WS.seen.indexOf(w.id) < 0) WS.seen.push(w.id);
}

/** 立刻放一整波（測試與除錯主控台用）。等 40 秒才看得到一次，驗證不了七種都正常。 */
export function runWave(id, a?, tel?) {
  var w = waveById(id);
  if (!w) return null;
  var ang = a === undefined ? rr(0, TAU) : a;
  /* 預告一起演。實看驗收要看的正是「預告指的方向與敵人來的方向是不是同一邊」，
     只放敵人的話那件事根本沒被驗到（第一輪截圖就是這樣白拍的）。 */
  if (tel) telegraph(ang);
  launch(w, ang);
  banner('來襲 · ' + w.n);
  radio(w.radio({ a: ang, dir: bearing(ang), t: STATE.t }));
  return w.n;
}

/** 只推進梯次佇列，不排新的波（測試用：驗「梯次是分批進場」不需要排程干擾）。 */
export function tickWaveQueue(dt) { tickQueue(dt); }

function tickQueue(dt) {
  for (var i = WS.q.length - 1; i >= 0; i--) {
    var it = WS.q[i];
    it.t -= dt;
    if (it.t > 0) continue;
    WS.q.splice(i, 1);
    it.run(it.c);
  }
}

export function updateWaves(dt) {
  var t = STATE.t;
  updateStream(dt);
  tickQueue(dt);
  if (t < WS.next) return;

  /* 預告先行。**沒有預告的波次是意外不是難度**（GDD §1 支柱一）。
     這裡刻意分兩步（先預告、下一步才放人）而不是「放人並同時畫預告」——
     一秒的時間差就是玩家做決定的窗口。 */
  if (!WS.warned) {
    var w0 = pick(t);
    WS.cur = w0.id;
    WS.a = rr(0, TAU);
    WS.warned = true; WS.warnT = 1.1;
    telegraph(WS.a);
    banner('來襲 · ' + w0.n);
    radio(w0.radio({ a: WS.a, dir: bearing(WS.a), t: t }));
    return;
  }
  WS.warnT -= dt;
  if (WS.warnT > 0) return;

  var w = waveById(WS.cur) || WAVES[0];
  launch(w, WS.a);
  WS.warned = false;
  WS.next = t + waveEvery(t);
}

/** 診斷用：這一場出現過幾種不同的波次。 */
export function waveKindsSeen() { return WS.seen.slice(); }
